import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Crop,
  Download,
  Image,
  Loader2,
  Printer,
  Ruler,
  Search,
} from 'lucide-react';
import Button from '../../components/Button';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthRefresh } from '../../hooks/useAuthRefresh';
import { getErrorMessage } from '../../utils/errorHandler';
import { API_BASE_URL } from '../../utils/apiUrl';
import { libraryService, Mockup } from '../../services/libraryService';
import {
  printPrepService,
  PrintBackgroundMode,
  PrintFitMode,
  PrintFormat,
  PrintUnits,
} from '../../services/printPrepService';

type Preset = {
  id: string;
  name: string;
  width: number;
  height: number;
  units: PrintUnits;
  bleed: number;
  safeMargin: number;
};

const PRINT_PRESETS: Preset[] = [
  { id: 'poster-18x24', name: 'Poster 18 x 24', width: 18, height: 24, units: 'in', bleed: 0.125, safeMargin: 0.25 },
  { id: 'poster-24x36', name: 'Poster 24 x 36', width: 24, height: 36, units: 'in', bleed: 0.125, safeMargin: 0.25 },
  { id: 'square-12x12', name: 'Square 12 x 12', width: 12, height: 12, units: 'in', bleed: 0.125, safeMargin: 0.25 },
  { id: 'postcard-6x4', name: 'Postcard 6 x 4', width: 6, height: 4, units: 'in', bleed: 0.125, safeMargin: 0.1875 },
  { id: 'a4', name: 'A4', width: 210, height: 297, units: 'mm', bleed: 3, safeMargin: 5 },
  { id: 'a3', name: 'A3', width: 297, height: 420, units: 'mm', bleed: 3, safeMargin: 5 },
  { id: 'a5', name: 'A5 / Flyer', width: 148, height: 210, units: 'mm', bleed: 3, safeMargin: 5 },
];

const DPI_OPTIONS = [150, 300, 600];
const MM_PER_INCH = 25.4;

const toImageUrl = (url: string) => {
  if (!url) {
    return '';
  }

  return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
};

const toPixels = (value: number, units: PrintUnits, dpi: number) => {
  const inches = units === 'mm' ? value / MM_PER_INCH : value;
  return Math.max(1, Math.round(inches * dpi));
};

const formatUnitValue = (value: number, units: PrintUnits) => {
  return units === 'mm' ? `${value.toFixed(1)} mm` : `${value.toFixed(3)} in`;
};

const qualityTone = (ppi: number | null) => {
  if (ppi === null) {
    return {
      label: 'Unknown',
      className: 'border-gray-200 bg-gray-50 text-gray-700',
      message: 'Source dimensions are missing, so print density cannot be verified.',
    };
  }

  if (ppi >= 300) {
    return {
      label: 'Print-ready',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      message: 'The selected mockup should hold up for professional print at this size.',
    };
  }

  if (ppi >= 240) {
    return {
      label: 'Good',
      className: 'border-sky-200 bg-sky-50 text-sky-700',
      message: 'Usable for most print jobs, but confirm with the printer before production.',
    };
  }

  if (ppi >= 180) {
    return {
      label: 'Fair',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
      message: 'This export may soften in print. Reduce the print size or use a higher-resolution source.',
    };
  }

  return {
    label: 'Low',
    className: 'border-red-200 bg-red-50 text-red-700',
    message: 'The selected mockup is likely too soft for reliable print output at this size.',
  };
};

const MockupToPrint: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const [mockups, setMockups] = useState<Mockup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState<string>(PRINT_PRESETS[0].id);
  const [units, setUnits] = useState<PrintUnits>(PRINT_PRESETS[0].units);
  const [width, setWidth] = useState<number>(PRINT_PRESETS[0].width);
  const [height, setHeight] = useState<number>(PRINT_PRESETS[0].height);
  const [bleed, setBleed] = useState<number>(PRINT_PRESETS[0].bleed);
  const [safeMargin, setSafeMargin] = useState<number>(PRINT_PRESETS[0].safeMargin);
  const [dpi, setDpi] = useState<number>(300);
  const [format, setFormat] = useState<PrintFormat>('png');
  const [fitMode, setFitMode] = useState<PrintFitMode>('contain');
  const [backgroundMode, setBackgroundMode] = useState<PrintBackgroundMode>('fill-white');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [exporting, setExporting] = useState(false);

  const selectedMockupId = searchParams.get('mockup');

  const loadMockups = async () => {
    if (!user) {
      setMockups([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { mockups: data, error } = await libraryService.getUserMockups({
        limit: 100,
      });

      if (error) {
        throw new Error(error);
      }

      setMockups(data);
    } catch (error) {
      showNotification('error', 'Unable to load library mockups', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useAuthRefresh(() => {
    if (!authLoading && user) {
      void loadMockups();
    }
  });

  useEffect(() => {
    if (authLoading) {
      return;
    }

    void loadMockups();
  }, [authLoading, user]);

  useEffect(() => {
    if (!mockups.length) {
      return;
    }

    if (selectedMockupId && mockups.some((mockup) => mockup.id === selectedMockupId)) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('mockup', mockups[0].id);
    setSearchParams(nextParams, { replace: true });
  }, [mockups, searchParams, selectedMockupId, setSearchParams]);

  const filteredMockups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return mockups;
    }

    return mockups.filter((mockup) => {
      return (
        mockup.name.toLowerCase().includes(query)
        || (mockup.template_name || '').toLowerCase().includes(query)
      );
    });
  }, [mockups, searchQuery]);

  const selectedMockup = useMemo(() => {
    return mockups.find((mockup) => mockup.id === selectedMockupId) || null;
  }, [mockups, selectedMockupId]);

  const dimensions = useMemo(() => {
    const trimWidthPx = toPixels(width, units, dpi);
    const trimHeightPx = toPixels(height, units, dpi);
    const bleedPx = toPixels(bleed, units, dpi);
    const safeMarginPx = toPixels(safeMargin, units, dpi);
    const finalWidthPx = trimWidthPx + bleedPx * 2;
    const finalHeightPx = trimHeightPx + bleedPx * 2;

    return {
      trimWidthPx,
      trimHeightPx,
      bleedPx,
      safeMarginPx,
      finalWidthPx,
      finalHeightPx,
    };
  }, [bleed, dpi, height, safeMargin, units, width]);

  const effectivePpi = useMemo(() => {
    if (!selectedMockup?.width || !selectedMockup?.height) {
      return null;
    }

    const scaleFactor = fitMode === 'cover'
      ? Math.max(
          dimensions.trimWidthPx / selectedMockup.width,
          dimensions.trimHeightPx / selectedMockup.height,
        )
      : Math.min(
          dimensions.trimWidthPx / selectedMockup.width,
          dimensions.trimHeightPx / selectedMockup.height,
        );

    if (!Number.isFinite(scaleFactor) || scaleFactor <= 0) {
      return null;
    }

    return dpi / scaleFactor;
  }, [dimensions.trimHeightPx, dimensions.trimWidthPx, dpi, fitMode, selectedMockup]);

  const readiness = qualityTone(effectivePpi);

  const warnings = useMemo(() => {
    const items: string[] = [];

    if (!selectedMockup) {
      items.push('Choose a mockup from your library before exporting.');
      return items;
    }

    if (!selectedMockup.width || !selectedMockup.height) {
      items.push('This mockup does not have stored source dimensions, so quality checks are limited.');
    }

    if (effectivePpi !== null && effectivePpi < 240) {
      items.push('Source density is below 240 PPI for this print size. The export may look soft.');
    }

    if (bleed > 0 && backgroundMode === 'preserve') {
      items.push('Transparent bleed may not meet printer requirements. Use a filled background if the piece trims edge-to-edge.');
    }

    if (format === 'jpg' && backgroundMode === 'preserve') {
      items.push('JPG does not support transparency. The package will flatten onto white.');
    }

    if (fitMode === 'cover') {
      items.push('Cover mode can crop the mockup to fill the trim area. Review the proof sheet before sending to print.');
    }

    return items;
  }, [backgroundMode, bleed, effectivePpi, fitMode, format, selectedMockup]);

  const handlePresetChange = (presetId: string) => {
    setSelectedPresetId(presetId);

    const preset = PRINT_PRESETS.find((item) => item.id === presetId);
    if (!preset) {
      return;
    }

    setUnits(preset.units);
    setWidth(preset.width);
    setHeight(preset.height);
    setBleed(preset.bleed);
    setSafeMargin(preset.safeMargin);
  };

  const handleSelectMockup = (mockupId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('mockup', mockupId);
    setSearchParams(nextParams, { replace: true });
  };

  const handleExport = async () => {
    if (!selectedMockup) {
      showNotification('error', 'No mockup selected', 'Choose a mockup to prepare for print.');
      return;
    }

    try {
      setExporting(true);
      await printPrepService.exportPackage(selectedMockup.name, {
        mockupId: selectedMockup.id,
        format,
        units,
        width,
        height,
        dpi,
        bleed,
        safeMargin,
        fitMode,
        backgroundMode,
        backgroundColor,
      });

      showNotification(
        'success',
        'Print package downloaded',
        'The ZIP includes the print-ready file, proof sheet, and production specs.',
      );
    } catch (error) {
      showNotification('error', 'Export failed', getErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6 md:p-8">
        <Loader2 className="h-8 w-8 animate-spin text-brand-purple" />
      </div>
    );
  }

  if (!mockups.length) {
    return (
      <div className="p-6 md:p-8">
        <div className="mx-auto max-w-3xl rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-sm">
          <Printer className="mx-auto h-14 w-14 text-brand-purple" />
          <h1 className="mt-6 text-3xl font-semibold text-gray-900">Mockup to Print</h1>
          <p className="mt-3 text-base text-gray-600">
            This tool works from your generated library mockups. Create or save a mockup first, then come back here to prepare a production package.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button to="/dashboard/mockups" variant="primary">
              Create Mockup
            </Button>
            <Button to="/dashboard/library" variant="secondary">
              Open Library
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <div className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white shadow-xl">
          <div className="grid gap-8 px-8 py-10 lg:grid-cols-[1.3fr_0.9fr] lg:px-10">
            <div>
              <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-sm font-semibold">
                <Printer className="mr-2 h-4 w-4" />
                Mockup to Print
              </div>
              <h1 className="mt-5 max-w-3xl text-4xl font-semibold tracking-tight">
                Turn finished mockups into print-ready production packages.
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-200">
                Select any generated mockup from your library, apply print dimensions and bleed, verify quality, then export a ZIP with the artwork, proof sheet, and job specs.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-6 backdrop-blur">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl bg-black/20 p-4">
                  <div className="flex items-center text-sm font-medium text-slate-200">
                    <Ruler className="mr-2 h-4 w-4" />
                    Trim
                  </div>
                  <p className="mt-3 text-2xl font-semibold">{formatUnitValue(width, units)}</p>
                  <p className="text-sm text-slate-300">{formatUnitValue(height, units)} high</p>
                </div>
                <div className="rounded-2xl bg-black/20 p-4">
                  <div className="flex items-center text-sm font-medium text-slate-200">
                    <Crop className="mr-2 h-4 w-4" />
                    Bleed
                  </div>
                  <p className="mt-3 text-2xl font-semibold">{formatUnitValue(bleed, units)}</p>
                  <p className="text-sm text-slate-300">each side</p>
                </div>
                <div className="rounded-2xl bg-black/20 p-4">
                  <div className="flex items-center text-sm font-medium text-slate-200">
                    <Image className="mr-2 h-4 w-4" />
                    Density
                  </div>
                  <p className="mt-3 text-2xl font-semibold">{dpi}</p>
                  <p className="text-sm text-slate-300">DPI export</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-8 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Choose a source mockup</h2>
                  <p className="mt-1 text-sm text-gray-500">This screen pulls from your generated library so the export stays tied to saved work.</p>
                </div>
                <Link to="/dashboard/library" className="text-sm font-semibold text-brand-purple hover:text-brand-purple/80">
                  Open full library
                </Link>
              </div>

              <div className="relative mt-5">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search saved mockups..."
                  className="w-full rounded-2xl border border-gray-300 py-3 pl-12 pr-4 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                />
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {filteredMockups.map((mockup) => {
                  const isSelected = mockup.id === selectedMockup?.id;

                  return (
                    <button
                      key={mockup.id}
                      type="button"
                      onClick={() => handleSelectMockup(mockup.id)}
                      className={`rounded-2xl border p-4 text-left transition-all ${
                        isSelected
                          ? 'border-brand-purple bg-brand-purple/5 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-gray-100">
                        <img
                          src={toImageUrl(mockup.thumbnail_url || mockup.mockup_url)}
                          alt={mockup.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="mt-4 flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">{mockup.name}</h3>
                          <p className="mt-1 text-sm text-gray-500 capitalize">
                            {mockup.template_name ? mockup.template_name.replace(/-/g, ' ') : 'Custom scene'}
                          </p>
                        </div>
                        {isSelected && (
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-purple text-white">
                            <CheckCircle2 className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
                        <span>{mockup.width && mockup.height ? `${mockup.width} x ${mockup.height}px` : 'Dimensions unavailable'}</span>
                        <span>{mockup.downloads} downloads</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedMockup && (
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-6 lg:flex-row">
                  <div className="lg:w-[58%]">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">Proof preview</h2>
                        <p className="mt-1 text-sm text-gray-500">This is a guide preview. The export package contains the full-resolution proof sheet.</p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${readiness.className}`}>
                        {readiness.label}
                      </span>
                    </div>

                    <div className="rounded-[28px] bg-slate-950 p-6">
                      <div
                        className="relative mx-auto rounded-lg border-2 border-amber-400 bg-white/95 shadow-2xl"
                        style={{
                          width: '100%',
                          maxWidth: '560px',
                          aspectRatio: `${dimensions.finalWidthPx} / ${dimensions.finalHeightPx}`,
                        }}
                      >
                        <div
                          className="absolute border-2 border-emerald-500"
                          style={{
                            left: `${(dimensions.bleedPx / dimensions.finalWidthPx) * 100}%`,
                            top: `${(dimensions.bleedPx / dimensions.finalHeightPx) * 100}%`,
                            width: `${(dimensions.trimWidthPx / dimensions.finalWidthPx) * 100}%`,
                            height: `${(dimensions.trimHeightPx / dimensions.finalHeightPx) * 100}%`,
                          }}
                        />
                        <div
                          className="absolute border-2 border-dashed border-pink-500"
                          style={{
                            left: `${((dimensions.bleedPx + dimensions.safeMarginPx) / dimensions.finalWidthPx) * 100}%`,
                            top: `${((dimensions.bleedPx + dimensions.safeMarginPx) / dimensions.finalHeightPx) * 100}%`,
                            width: `${(Math.max(1, dimensions.trimWidthPx - dimensions.safeMarginPx * 2) / dimensions.finalWidthPx) * 100}%`,
                            height: `${(Math.max(1, dimensions.trimHeightPx - dimensions.safeMarginPx * 2) / dimensions.finalHeightPx) * 100}%`,
                          }}
                        />
                        <img
                          src={toImageUrl(selectedMockup.mockup_url)}
                          alt={selectedMockup.name}
                          className="absolute"
                          style={{
                            left: `${(dimensions.bleedPx / dimensions.finalWidthPx) * 100}%`,
                            top: `${(dimensions.bleedPx / dimensions.finalHeightPx) * 100}%`,
                            width: `${(dimensions.trimWidthPx / dimensions.finalWidthPx) * 100}%`,
                            height: `${(dimensions.trimHeightPx / dimensions.finalHeightPx) * 100}%`,
                            objectFit: fitMode === 'cover' ? 'cover' : 'contain',
                            background:
                              backgroundMode === 'fill-custom'
                                ? backgroundColor
                                : backgroundMode === 'fill-white' || format === 'jpg'
                                ? '#FFFFFF'
                                : 'transparent',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-[42%]">
                    <h3 className="text-lg font-semibold text-gray-900">Production summary</h3>
                    <p className="mt-1 text-sm text-gray-500">{readiness.message}</p>

                    <div className="mt-5 space-y-3">
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-sm font-medium text-gray-600">Selected source</p>
                        <p className="mt-1 text-base font-semibold text-gray-900">{selectedMockup.name}</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {selectedMockup.width && selectedMockup.height
                            ? `${selectedMockup.width} x ${selectedMockup.height} px`
                            : 'Stored dimensions unavailable'}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-sm font-medium text-gray-600">Final export canvas</p>
                        <p className="mt-1 text-base font-semibold text-gray-900">
                          {dimensions.finalWidthPx} x {dimensions.finalHeightPx} px
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          Trim {dimensions.trimWidthPx} x {dimensions.trimHeightPx}px plus bleed on every side
                        </p>
                      </div>
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-sm font-medium text-gray-600">Estimated source density</p>
                        <p className="mt-1 text-base font-semibold text-gray-900">
                          {effectivePpi === null ? 'Unknown' : `${effectivePpi.toFixed(0)} PPI`}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          Based on the stored source size and the selected trim dimensions
                        </p>
                      </div>
                    </div>

                    {warnings.length > 0 && (
                      <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-center text-sm font-semibold text-amber-800">
                          <AlertTriangle className="mr-2 h-4 w-4" />
                          Review before export
                        </div>
                        <div className="mt-3 space-y-2 text-sm text-amber-800">
                          {warnings.map((warning) => (
                            <p key={warning}>{warning}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6 xl:sticky xl:top-8 xl:self-start">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900">Print setup</h2>
              <p className="mt-1 text-sm text-gray-500">Set physical size, bleed, output format, and placement behavior.</p>

              <div className="mt-6 space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Preset</label>
                  <select
                    value={selectedPresetId}
                    onChange={(event) => handlePresetChange(event.target.value)}
                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                  >
                    {PRINT_PRESETS.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Units</label>
                    <select
                      value={units}
                      onChange={(event) => setUnits(event.target.value as PrintUnits)}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                    >
                      <option value="in">Inches</option>
                      <option value="mm">Millimeters</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">DPI</label>
                    <select
                      value={dpi}
                      onChange={(event) => setDpi(Number(event.target.value))}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                    >
                      {DPI_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option} DPI
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Width</label>
                    <input
                      type="number"
                      min="0.1"
                      step={units === 'mm' ? '1' : '0.125'}
                      value={width}
                      onChange={(event) => setWidth(Number(event.target.value))}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Height</label>
                    <input
                      type="number"
                      min="0.1"
                      step={units === 'mm' ? '1' : '0.125'}
                      value={height}
                      onChange={(event) => setHeight(Number(event.target.value))}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Bleed per side</label>
                    <input
                      type="number"
                      min="0"
                      step={units === 'mm' ? '1' : '0.03125'}
                      value={bleed}
                      onChange={(event) => setBleed(Number(event.target.value))}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Safe margin</label>
                    <input
                      type="number"
                      min="0"
                      step={units === 'mm' ? '1' : '0.03125'}
                      value={safeMargin}
                      onChange={(event) => setSafeMargin(Number(event.target.value))}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Output format</label>
                    <select
                      value={format}
                      onChange={(event) => setFormat(event.target.value as PrintFormat)}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                    >
                      <option value="png">PNG</option>
                      <option value="jpg">JPG</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Placement</label>
                    <select
                      value={fitMode}
                      onChange={(event) => setFitMode(event.target.value as PrintFitMode)}
                      className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                    >
                      <option value="contain">Contain inside trim</option>
                      <option value="cover">Cover trim area</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Background handling</label>
                  <select
                    value={backgroundMode}
                    onChange={(event) => setBackgroundMode(event.target.value as PrintBackgroundMode)}
                    className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                  >
                    <option value="fill-white">Fill white</option>
                    <option value="fill-custom">Fill custom color</option>
                    <option value="preserve">Preserve transparency</option>
                  </select>
                </div>

                {backgroundMode === 'fill-custom' && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Background color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(event) => setBackgroundColor(event.target.value)}
                        className="h-12 w-16 rounded-xl border border-gray-300 bg-white"
                      />
                      <input
                        type="text"
                        value={backgroundColor}
                        onChange={(event) => setBackgroundColor(event.target.value)}
                        className="flex-1 rounded-2xl border border-gray-300 px-4 py-3 text-sm uppercase focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900">Package contents</h2>
              <div className="mt-4 space-y-3 text-sm text-gray-600">
                <div className="flex items-start rounded-2xl bg-gray-50 p-4">
                  <CheckCircle2 className="mr-3 mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <p>Full-resolution artwork sized to your print spec.</p>
                </div>
                <div className="flex items-start rounded-2xl bg-gray-50 p-4">
                  <CheckCircle2 className="mr-3 mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <p>Proof sheet with bleed, trim, and safe-area guides over the exported piece.</p>
                </div>
                <div className="flex items-start rounded-2xl bg-gray-50 p-4">
                  <CheckCircle2 className="mr-3 mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <p>Production notes with final dimensions, export settings, and printer reminders.</p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-gray-200 bg-slate-50 p-4">
                <p className="text-sm font-medium text-gray-700">Final canvas</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {dimensions.finalWidthPx} x {dimensions.finalHeightPx}
                </p>
                <p className="mt-1 text-sm text-gray-500">pixels including bleed</p>
              </div>

              <button
                type="button"
                onClick={handleExport}
                disabled={!selectedMockup || exporting}
                className="mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-accent-gradient px-5 py-4 text-sm font-semibold text-white transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Preparing print package...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download Print Package
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockupToPrint;
